/**
 * v4 Money — full financial intelligence page. Combines what v1 had as
 * "Money" + "Insights" into one comprehensive view, with refined v4 styling.
 *
 * Sections:
 *   1. Period picker + hero KPI grid
 *   2. Revenue & profit 6-month trend (LineChart)
 *   3. Revenue mix donut (PieChart)
 *   4. Cashflow health gauge (RadialBarChart) + side metrics
 *   5. Top expenses (BarChart)
 *   6. Industry benchmarks (vs similar businesses)
 *   7. Deduction tips (worth discussing with your accountant)
 */

import { useState } from 'react';
import {
  LineChart, Line, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip,
  PieChart, Pie, Cell, RadialBarChart, RadialBar,
} from 'recharts';
import {
  TrendingUp, TrendingDown, Lightbulb, BarChart3, Target, PieChart as PieIcon,
  Activity,
} from 'lucide-react';
import { useV4 } from './i18n';
import { cn } from '@/lib/utils';

type Range = 'week' | 'month' | 'year';

interface RangeData {
  revenue: number; expenses: number; profit: number;
  marginPct: number; delta: number;
}

const RANGE_DATA: Record<Range, RangeData> = {
  week:  { revenue: 4820,   expenses: 3180,   profit: 1640,  marginPct: 34.0, delta: 12 },
  month: { revenue: 21240,  expenses: 15870,  profit: 5370,  marginPct: 25.3, delta: 8 },
  year:  { revenue: 264800, expenses: 198600, profit: 66200, marginPct: 25.0, delta: 5 },
};

// 6-month trend
const REVENUE_TREND = [
  { month: 'Nov', revenue: 17400, profit: 3200 },
  { month: 'Dec', revenue: 19200, profit: 4100 },
  { month: 'Jan', revenue: 16800, profit: 2900 },
  { month: 'Feb', revenue: 18900, profit: 3800 },
  { month: 'Mar', revenue: 19800, profit: 4200 },
  { month: 'Apr', revenue: 21240, profit: 5370 },
];

// Revenue mix by service line
const REVENUE_MIX = [
  { name: 'Residential tiling',  value: 142000, color: '#6366f1' },
  { name: 'Commercial projects', value: 88000,  color: '#10b981' },
  { name: 'Repairs & callouts',  value: 38500,  color: '#f59e0b' },
  { name: 'Other',               value: 16000,  color: '#94a3b8' },
];

// Cashflow gauge — 0-100
const CASHFLOW_GAUGE = [{ name: 'Cashflow health', value: 72, fill: '#10b981' }];

// Top expenses (year-to-date)
const EXPENSES = [
  { name: 'Materials',          amount: 124000 },
  { name: 'Rent & utilities',   amount: 33600 },
  { name: 'Wages & super',      amount: 25200 },
  { name: 'Subcontractor',      amount: 11200 },
  { name: 'Motor vehicle',      amount: 8420 },
];

const BENCHMARKS = [
  { metric: 'Profit margin',     you: 25.3, industry: 17.4, unit: '%', higherIsBetter: true },
  { metric: 'Materials / sales', you: 47.0, industry: 51.2, unit: '%', higherIsBetter: false },
  { metric: 'Rent / sales',      you: 12.6, industry: 14.8, unit: '%', higherIsBetter: false },
];

const DEDUCTION_TIPS = [
  { title: 'Home office expenses', detail: 'You may claim a portion of internet, electricity and phone bills if you spend time on admin from home.' },
  { title: 'Vehicle logbook', detail: 'Keeping a 12-week logbook this quarter can boost the deduction you claim for the next 5 years.' },
  { title: 'Instant asset write-off', detail: 'Equipment under $20,000 may be immediately deductible. Worth reviewing before 30 June.' },
];

export default function MoneyPageV4() {
  const { t, fmtCurrency, themeMode, accent } = useV4();
  const [range, setRange] = useState<Range>('month');
  const d = RANGE_DATA[range];

  // Theme-aware recharts colors
  const isDark = themeMode === 'dark';
  const axisColor   = isDark ? '#94a3b8' : '#78716c';
  const tooltipBg   = isDark ? '#1e293b' : '#ffffff';
  const tooltipEdge = isDark ? '#334155' : '#e7e5e4';
  const tooltipText = isDark ? '#e2e8f0' : '#0f172a';
  const profitLine  = isDark ? '#94a3b8' : '#78716c';
  const gaugeTrack  = isDark ? '#334155' : '#f5f5f4';
  const ttipStyle = { borderRadius: 12, border: `1px solid ${tooltipEdge}`, background: tooltipBg, color: tooltipText, fontSize: 12 };

  return (
    <div className="space-y-3 pb-4">
      {/* Section header + range picker */}
      <div className="flex items-center justify-between pt-1">
        <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">{t('money_title')}</p>
        <RangePicker value={range} onChange={setRange} t={t} />
      </div>

      {/* Hero — revenue + delta + KPI strip */}
      <div className="rounded-2xl border border-border bg-card p-5 shadow-sm">
        <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">{t('revenue')}</p>
        <p className="mt-1 text-4xl font-bold tabular-nums tracking-tight text-foreground">{fmtCurrency(d.revenue)}</p>
        <div className="mt-1.5 inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 dark:text-emerald-300">
          <TrendingUp className="h-3 w-3" />
          <span>+{d.delta}%</span>
          <span className="font-normal text-muted-foreground">{t('vs_last_period')}</span>
        </div>
        <div className="mt-4 grid grid-cols-3 gap-3 border-t border-border/60 pt-4">
          <KPI label={t('expenses')} value={fmtCurrency(d.expenses)} variant="negative" />
          <KPI label={t('profit')}   value={fmtCurrency(d.profit)}   variant="positive" />
          <KPI label={t('margin')}   value={`${d.marginPct.toFixed(1)}%`} variant="accent" />
        </div>
      </div>

      {/* Revenue & Profit trend */}
      <Card>
        <SectionHeader
          icon={Activity}
          title={t('revenue_profit_trend')}
          subtitle={t('last_6_months')}
          badge={<TrendBadge value={12.4} />}
        />
        <div className="h-44 px-2">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={REVENUE_TREND}>
              <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: axisColor }} />
              <YAxis hide />
              <Tooltip
                contentStyle={ttipStyle}
                formatter={(v: number) => `$${v.toLocaleString()}`}
              />
              <Line type="monotone" dataKey="revenue" stroke="#10b981" strokeWidth={2.5} dot={{ r: 3, fill: '#10b981' }} activeDot={{ r: 5 }} />
              <Line type="monotone" dataKey="profit"  stroke={profitLine} strokeWidth={2} strokeDasharray="4 4" dot={{ r: 2, fill: profitLine }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
        <div className="flex items-center gap-4 px-4 pb-3 pt-1 text-xs">
          <Legend color="#10b981" label={t('revenue')} />
          <Legend color={profitLine} label={t('profit')} />
        </div>
      </Card>

      {/* Revenue mix donut */}
      <Card>
        <SectionHeader icon={PieIcon} title={t('revenue_mix')} subtitle={t('by_service_line')} />
        <div className="flex items-center gap-4 p-4">
          <div className="h-36 w-36 flex-shrink-0">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={REVENUE_MIX} dataKey="value" innerRadius={40} outerRadius={65} paddingAngle={2} stroke="none">
                  {REVENUE_MIX.map((d, i) => <Cell key={i} fill={d.color} />)}
                </Pie>
                <Tooltip
                  contentStyle={ttipStyle}
                  formatter={(v: number) => `$${v.toLocaleString()}`}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="flex-1 space-y-2">
            {REVENUE_MIX.map(m => {
              const total = REVENUE_MIX.reduce((s, x) => s + x.value, 0);
              const pct = Math.round((m.value / total) * 100);
              return (
                <div key={m.name} className="flex items-center gap-2 text-xs">
                  <span className="h-2 w-2 flex-shrink-0 rounded-full" style={{ background: m.color }} />
                  <span className="flex-1 truncate text-foreground/80">{m.name}</span>
                  <span className="font-semibold tabular-nums text-foreground">{pct}%</span>
                </div>
              );
            })}
          </div>
        </div>
      </Card>

      {/* Cashflow health gauge */}
      <Card>
        <SectionHeader icon={Activity} title={t('cashflow_health')} subtitle={t('cashflow_hint')} />
        <div className="flex items-center gap-4 p-4">
          <div className="relative h-32 w-32 flex-shrink-0">
            <ResponsiveContainer width="100%" height="100%">
              <RadialBarChart innerRadius="70%" outerRadius="100%" data={CASHFLOW_GAUGE} startAngle={210} endAngle={-30}>
                <RadialBar background={{ fill: gaugeTrack }} dataKey="value" cornerRadius={20} />
              </RadialBarChart>
            </ResponsiveContainer>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <span className="text-3xl font-bold tabular-nums text-foreground">72</span>
              <span className="text-[10px] font-semibold uppercase tracking-wider text-emerald-700 dark:text-emerald-300">{t('healthy')}</span>
            </div>
          </div>
          <div className="flex-1 space-y-2 text-xs">
            <PillStat label={t('days_runway')} value="94" />
            <PillStat label={t('outstanding_inv')} value={fmtCurrency(12480)} />
            <PillStat label={t('bills_due_30d')} value={fmtCurrency(4310)} />
          </div>
        </div>
      </Card>

      {/* Top expenses bar chart */}
      <Card>
        <SectionHeader icon={BarChart3} title={t('where_money_goes')} subtitle={t('top_expenses')} />
        <div className="h-52 px-2 pb-2">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={EXPENSES} layout="vertical" margin={{ left: 0, right: 20 }}>
              <XAxis type="number" hide />
              <YAxis
                type="category" dataKey="name"
                axisLine={false} tickLine={false}
                tick={{ fontSize: 11, fill: axisColor }}
                width={120}
              />
              <Tooltip
                contentStyle={ttipStyle}
                formatter={(v: number) => `$${v.toLocaleString()}`}
              />
              <Bar dataKey="amount" fill={accent.primary} radius={[6, 6, 6, 6]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Card>

      {/* Industry benchmarks */}
      <Card>
        <SectionHeader icon={Target} title={t('how_you_compare')} subtitle={t('vs_similar_biz')} />
        <div className="space-y-4 p-4">
          {BENCHMARKS.map(b => {
            const better = b.higherIsBetter ? b.you > b.industry : b.you < b.industry;
            const maxVal = Math.max(b.you, b.industry);
            const youPct = (b.you / maxVal) * 100;
            const indPct = (b.industry / maxVal) * 100;
            return (
              <div key={b.metric}>
                <div className="mb-1.5 flex items-center justify-between">
                  <span className="text-xs font-semibold text-foreground">{b.metric}</span>
                  <span className={cn(
                    'inline-flex items-center gap-1 text-[11px] font-semibold',
                    better ? 'text-emerald-700 dark:text-emerald-300' : 'text-rose-600 dark:text-rose-400'
                  )}>
                    {better ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
                    {better ? t('above_avg') : t('below_avg')}
                  </span>
                </div>
                <div className="space-y-1.5">
                  <div className="flex items-center gap-2">
                    <span className="w-10 text-[10px] font-bold uppercase text-foreground/80">{t('you')}</span>
                    <div className="flex-1 overflow-hidden rounded-full bg-muted">
                      <div className={cn('h-2 rounded-full', better ? 'bg-emerald-500' : 'bg-rose-500')} style={{ width: `${youPct}%` }} />
                    </div>
                    <span className="w-12 text-end text-xs font-bold tabular-nums text-foreground">{b.you}{b.unit}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="w-10 text-[10px] font-bold uppercase text-muted-foreground/60">{t('avg')}</span>
                    <div className="flex-1 overflow-hidden rounded-full bg-muted">
                      <div className="h-2 rounded-full bg-muted-foreground/40" style={{ width: `${indPct}%` }} />
                    </div>
                    <span className="w-12 text-end text-xs tabular-nums text-muted-foreground">{b.industry}{b.unit}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </Card>

      {/* Deduction tips */}
      <div className="rounded-2xl border border-amber-300 dark:border-amber-900/60 bg-gradient-to-br from-amber-50 to-yellow-50 dark:from-amber-950/40 dark:to-amber-950/20 p-4">
        <div className="flex items-center gap-2">
          <Lightbulb className="h-4 w-4 text-amber-700 dark:text-amber-300" />
          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-amber-800 dark:text-amber-300">{t('worth_discussing')}</p>
        </div>
        <p className="mt-1 text-[11px] text-amber-700 dark:text-amber-300">{t('worth_discussing_hint')}</p>
        <div className="mt-3 space-y-2">
          {DEDUCTION_TIPS.map((tip, i) => (
            <div key={i} className="rounded-xl bg-card p-3 border border-amber-100 dark:border-amber-900/40">
              <p className="text-sm font-semibold text-foreground">{tip.title}</p>
              <p className="mt-1 text-xs leading-relaxed text-muted-foreground">{tip.detail}</p>
            </div>
          ))}
        </div>
        <p className="mt-3 text-[10px] italic text-amber-700 dark:text-amber-300">{t('tips_general_guidance')}</p>
      </div>
    </div>
  );
}

/* ─── small parts ─── */

function Card({ children }: { children: React.ReactNode }) {
  return <div className="rounded-2xl border border-border bg-card">{children}</div>;
}

function SectionHeader({ icon: Icon, title, subtitle, badge }: { icon: any; title: string; subtitle: string; badge?: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between px-4 pt-3 pb-1">
      <div className="flex items-center gap-2">
        <Icon className="h-4 w-4 text-foreground/80" />
        <div>
          <p className="text-sm font-semibold text-foreground">{title}</p>
          <p className="text-[10px] text-muted-foreground">{subtitle}</p>
        </div>
      </div>
      {badge}
    </div>
  );
}

function TrendBadge({ value }: { value: number }) {
  const positive = value >= 0;
  return (
    <span className={cn(
      'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold',
      positive ? 'bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300' : 'bg-rose-50 dark:bg-rose-950/40 text-rose-600 dark:text-rose-400'
    )}>
      {positive ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
      {positive ? '+' : ''}{value}%
    </span>
  );
}

function Legend({ color, label }: { color: string; label: string }) {
  return (
    <div className="flex items-center gap-1.5">
      <span className="h-2 w-2 rounded-full" style={{ background: color }} />
      <span className="text-muted-foreground">{label}</span>
    </div>
  );
}

function PillStat({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between rounded-lg bg-background px-3 py-1.5">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-bold tabular-nums text-foreground">{value}</span>
    </div>
  );
}

function RangePicker({ value, onChange, t }: { value: Range; onChange: (r: Range) => void; t: (k: string) => string }) {
  const opts: { v: Range; l: string }[] = [
    { v: 'week',  l: t('this_week') },
    { v: 'month', l: t('this_month') },
    { v: 'year',  l: t('this_year') },
  ];
  return (
    <div className="inline-flex items-center gap-0.5 rounded-lg border border-border bg-card p-0.5 text-[11px]">
      {opts.map(o => (
        <button key={o.v} onClick={() => onChange(o.v)}
          className={cn(
            'rounded-md px-2.5 py-1 font-semibold transition-colors',
            value === o.v ? 'bg-[var(--v4-accent)] text-white' : 'text-muted-foreground hover:text-foreground'
          )}
        >
          {o.l}
        </button>
      ))}
    </div>
  );
}

function KPI({ label, value, variant }: { label: string; value: string; variant: 'positive' | 'negative' | 'accent' }) {
  return (
    <div>
      <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">{label}</p>
      <p className={cn(
        'mt-0.5 text-base font-bold tabular-nums',
        variant === 'positive' && 'text-foreground',
        variant === 'negative' && 'text-foreground/80',
        variant === 'accent'   && 'text-[var(--v4-accent-text)]',
      )}>
        {value}
      </p>
    </div>
  );
}
